import React, { useState, useEffect, useReducer, useContext, useRef, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import CloseIcon from "@material-ui/icons/Close";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import PushPinIcon from "@mui/icons-material/PushPin";

import GroupPermissionsModal from "../GroupPermissionsModal";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastUtils from "../../utils/toastUtils";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: theme.palette.text.primary,
        zIndex: 2,
        backgroundColor: theme.palette.background.paper,
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: theme.palette.text.secondary,
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: theme.palette.text.secondary,
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    selectionToolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 8px",
        backgroundColor: theme.palette.type === 'dark' ? '#263238' : '#e8f4fd',
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        zIndex: 3,
    },
    selectionText: {
        fontWeight: 600,
        marginLeft: 4,
        color: theme.palette.type === 'dark' ? '#90caf9' : '#1976d2',
        fontSize: "0.82rem",
    },
}));

const ticketSortAsc = (a, b) => {
    const aPinned = Boolean(a.isPinned);
    const bPinned = Boolean(b.isPinned);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    if (aPinned && bPinned) {
        const aTime = a.pinnedAt ? new Date(a.pinnedAt).getTime() : 0;
        const bTime = b.pinnedAt ? new Date(b.pinnedAt).getTime() : 0;
        if (bTime !== aTime) return bTime - aTime;
    }
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
};

const ticketSortDesc = (a, b) => {
    const aPinned = Boolean(a.isPinned);
    const bPinned = Boolean(b.isPinned);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    if (aPinned && bPinned) {
        const aTime = a.pinnedAt ? new Date(a.pinnedAt).getTime() : 0;
        const bTime = b.pinnedAt ? new Date(b.pinnedAt).getTime() : 0;
        if (bTime !== aTime) return bTime - aTime;
    }
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
};

const applySorting = (state, sortDir) => {
    if (sortDir === 'ASC') {
        state.sort(ticketSortAsc);
    } else {
        state.sort(ticketSortDesc);
    }
};

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });
        applySorting(state, sortDir);

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        applySorting(state, sortDir);

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            // Merge inteligente: preservar dados existentes quando os novos estiverem vazios
            const existingTicket = state[ticketIndex];
            state[ticketIndex] = {
                ...existingTicket,
                ...ticket,
                contact: ticket.contact?.id ? {
                    ...existingTicket.contact,
                    ...ticket.contact,
                    tags: ticket.contact?.tags?.length ? ticket.contact.tags : existingTicket.contact?.tags || []
                } : existingTicket.contact,
                whatsapp: ticket.whatsapp?.id ? ticket.whatsapp : existingTicket.whatsapp,
                queue: ticket.queue?.id ? ticket.queue : existingTicket.queue,
                user: ticket.user?.id ? ticket.user : existingTicket.user,
                tags: ticket.tags?.length ? ticket.tags : existingTicket.tags || [],
                lastMessage: ticket.lastMessage || action.lastMessage || existingTicket.lastMessage,
                channel: ticket.channel || existingTicket.channel,
                updatedAt: ticket.updatedAt || new Date().toISOString()
            };
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === ticket.status || (action.status === "group" && (ticket.isGroup || ticket.status === "group"))) {
                state.unshift({
                    ...ticket,
                    lastMessage: ticket.lastMessage || action.lastMessage,
                    updatedAt: ticket.updatedAt || new Date().toISOString()
                });
            }
        }
        applySorting(state, sortDir);

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            // Merge inteligente: preservar dados existentes quando os novos estiverem vazios
            const existingTicket = state[ticketIndex];
            state[ticketIndex] = {
                ...existingTicket,
                ...ticket,
                // Preservar campos que podem vir vazios/incompletos do WebSocket
                contact: ticket.contact?.id ? {
                    ...existingTicket.contact,
                    ...ticket.contact,
                    tags: ticket.contact?.tags?.length ? ticket.contact.tags : existingTicket.contact?.tags || []
                } : existingTicket.contact,
                whatsapp: ticket.whatsapp?.id ? ticket.whatsapp : existingTicket.whatsapp,
                queue: ticket.queue?.id ? ticket.queue : existingTicket.queue,
                user: ticket.user?.id ? ticket.user : existingTicket.user,
                tags: ticket.tags?.length ? ticket.tags : existingTicket.tags || [],
                lastMessage: ticket.lastMessage || action.lastMessage || existingTicket.lastMessage,
                channel: ticket.channel || existingTicket.channel,
                updatedAt: ticket.updatedAt || new Date().toISOString()
            };
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === ticket.status || (action.status === "group" && (ticket.isGroup || ticket.status === "group"))) {
                state.unshift({
                    ...ticket,
                    lastMessage: ticket.lastMessage || action.lastMessage,
                    updatedAt: ticket.updatedAt || new Date().toISOString()
                });
            }
        }
        applySorting(state, sortDir);

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        applySorting(state, sortDir);

        return [...state];
    }

    if (action.type === "BATCH_UPDATE_TICKETS") {
        const updatedTickets = action.payload || [];
        const updatedMap = new Map(updatedTickets.map(t => [t.id, t]));
        state.forEach((existingTicket, index) => {
            if (updatedMap.has(existingTicket.id)) {
                const updated = updatedMap.get(existingTicket.id);
                state[index] = {
                    ...existingTicket,
                    ...updated,
                    isMuted: updated.isMuted !== undefined ? updated.isMuted : existingTicket.isMuted,
                    isPinned: updated.isPinned !== undefined ? updated.isPinned : existingTicket.isPinned,
                    pinnedAt: updated.pinnedAt !== undefined ? updated.pinnedAt : existingTicket.pinnedAt,
                };
            }
        });
        applySorting(state, sortDir);
        return [...state];
    }

    if (action.type === "BATCH_UPDATE_MUTE") {
        const { ticketIds, isMuted } = action.payload;
        const set = new Set(ticketIds);
        state.forEach((t, i) => {
            if (set.has(t.id)) {
                state[i] = { ...t, isMuted };
            }
        });
        return [...state];
    }

    if (action.type === "BATCH_UPDATE_PIN") {
        const { ticketIds, isPinned } = action.payload;
        const set = new Set(ticketIds);
        const now = new Date().toISOString();
        state.forEach((t, i) => {
            if (set.has(t.id)) {
                state[i] = { ...t, isPinned, pinnedAt: isPinned ? now : null };
            }
        });
        applySorting(state, sortDir);
        return [...state];
    }

    if (action.type === "TAB_MUTE_ALL") {
        const { isMuted } = action.payload;
        state.forEach((t, i) => {
            state[i] = { ...t, isMuted };
        });
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        selectionMode: parentSelectionMode,
        setSelectionMode: setParentSelectionMode,
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    const ticketsListRef = useRef(ticketsList);
    useEffect(() => {
        ticketsListRef.current = ticketsList;
    }, [ticketsList]);

    const [localSelectionMode, setLocalSelectionMode] = useState(false);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [groupPermissionsModalOpen, setGroupPermissionsModalOpen] = useState(false);

    const selectedGroupContactIds = useMemo(() => {
        return ticketsList
            .filter((t) => selectedTickets.includes(t.id) && (t.contactId || t.contact?.id))
            .map((t) => t.contactId || t.contact?.id);
    }, [ticketsList, selectedTickets]);

    const isSelectionMode = parentSelectionMode !== undefined ? parentSelectionMode : localSelectionMode;
    const setSelectionMode = setParentSelectionMode || setLocalSelectionMode;

    useEffect(() => {
        if (!isSelectionMode) {
            setSelectedTickets([]);
        }
    }, [isSelectionMode]);

    const handleToggleSelect = (ticketId) => {
        setSelectedTickets((prev) => {
            if (prev.includes(ticketId)) {
                const next = prev.filter((id) => id !== ticketId);
                if (next.length === 0 && !parentSelectionMode) {
                    setSelectionMode(false);
                }
                return next;
            } else {
                return [...prev, ticketId];
            }
        });
    };

    const handleEnableSelection = (ticketId) => {
        setSelectionMode(true);
        setSelectedTickets([ticketId]);
    };

    const handleSelectAll = () => {
        if (selectedTickets.length === ticketsList.length) {
            setSelectedTickets([]);
        } else {
            setSelectedTickets(ticketsList.map((t) => t.id));
        }
    };

    const handleCancelSelection = () => {
        setSelectedTickets([]);
        setSelectionMode(false);
    };

    const handleBatchMute = async (isMuted) => {
        if (selectedTickets.length === 0) return;
        const ticketIdsToUpdate = [...selectedTickets];
        dispatch({
            type: "BATCH_UPDATE_MUTE",
            payload: { ticketIds: ticketIdsToUpdate, isMuted }
        });
        try {
            await api.post("/tickets/batch/mute", {
                ticketIds: ticketIdsToUpdate,
                isMuted
            });
            toastUtils.success(
                isMuted ? "Silenciado" : "Reativado",
                `${ticketIdsToUpdate.length} conversa(s) ${isMuted ? "silenciadas" : "reativadas"} com sucesso!`
            );
            handleCancelSelection();
        } catch (err) {
            toastError(err);
        }
    };

    const handleBatchPin = async (isPinned) => {
        if (selectedTickets.length === 0) return;
        const ticketIdsToUpdate = [...selectedTickets];
        dispatch({
            type: "BATCH_UPDATE_PIN",
            payload: { ticketIds: ticketIdsToUpdate, isPinned }
        });
        try {
            await api.post("/tickets/batch/pin", {
                ticketIds: ticketIdsToUpdate,
                isPinned
            });
            toastUtils.success(
                isPinned ? "Fixado" : "Desafixado",
                `${ticketIdsToUpdate.length} conversa(s) ${isPinned ? "fixadas no topo" : "desafixadas"} com sucesso!`
            );
            handleCancelSelection();
        } catch (err) {
            toastError(err);
        }
    };
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages]);

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickets]);

    useEffect(() => {
        const shouldUpdateTicket = ticket => {
            // Não atualizar tickets durante busca ativa
            const isSearch = searchParam && searchParam.trim().length > 0;
            if (isSearch) {
                return false;
            }

            // ✅ SE FOR TICKET DE GRUPO:
            // Grupos não pertencem a filas (queueId) e não têm atendente exclusivo.
            const isGroup = ticket?.isGroup || ticket?.status === "group";
            if (status === "group") {
                if (!isGroup) return false;
                if (user?.profile === "admin") return true;

                // Usuário comum:
                const hasAssignedUsers = ticket?.contact?.users && ticket.contact.users.length > 0;
                if (!hasAssignedUsers) {
                    // Sem usuários especificados: todos os usuários podem ver
                    return true;
                }

                // Com usuários especificados: apenas os usuários associados a este grupo podem ver
                return ticket.contact.users.some(u => u.id === user?.id);
            }

            // Se este TicketsList é para status individual (open/pending), não aceitar grupos
            if (isGroup && status !== "group") {
                return false;
            }

            // Verificar se o ticket pertence ao usuário atual ou se showAll está ativo
            const isCurrentUserTicket = ticket?.userId === user?.id;
            const isAcceptedParticipant = ticket?.participants?.some(
                p => p.userId === user?.id && p.status === "accepted"
            );
            const hasNoUser = !ticket?.userId;
            const isShowAllActive = showAll;
            
            // Para tickets pending criados via API, permitir exibição mesmo sem userId
            const isPendingTicket = ticket?.status === "pending";
            const userCondition = isPendingTicket 
                ? (hasNoUser || isCurrentUserTicket || isShowAllActive) 
                : (isCurrentUserTicket || isAcceptedParticipant || isShowAllActive);
            
            // Verificar se o ticket deve ser exibido com base na fila
            const hasNoQueue = !ticket?.queueId;
            const isQueueSelected = selectedQueueIds.indexOf(ticket?.queueId) > -1;
            const queueCondition = (hasNoQueue && showTicketWithoutQueue) || isQueueSelected;

            return userCondition && queueCondition;
        }
        
        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // console.log(shouldUpdateTicket(data.ticket))
            if (data.action === "update" && data.ticket) {
                const isGroup = data.ticket.isGroup || data.ticket.status === "group";
                const isMatchStatus = data.ticket.status === status || (status === "group" && isGroup);

                if (shouldUpdateTicket(data.ticket) && isMatchStatus) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets
                    });
                } else if (!isMatchStatus || !shouldUpdateTicket(data.ticket)) {
                    // ✅ Se o status do ticket mudou OU o ticket não pertence mais a este atendente (ex: removido do co-atendimento), remover da lista atual
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: data.ticket?.id,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }

            if (data.action === "batchUpdate" && Array.isArray(data.tickets)) {
                dispatch({
                    type: "BATCH_UPDATE_TICKETS",
                    payload: data.tickets,
                    status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "update" && notBelongsToUserQueues(data.ticket) && !data.ticket.isGroup && status !== "group") {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });

            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            const ticketData = data.ticket || data.message?.ticket;
            const messageBody = data.message?.body;

            if (data.action === "create" && ticketData) {
                const isGroup = ticketData.isGroup || ticketData.status === "group";
                const isMatchStatus = ticketData.status === status || (status === "group" && isGroup);

                const updatedTicket = {
                    ...ticketData,
                    lastMessage: messageBody || ticketData.lastMessage,
                    updatedAt: data.message?.createdAt || new Date().toISOString()
                };

                if (shouldUpdateTicket(updatedTicket) && isMatchStatus) {
                    dispatch({
                        type: "UPDATE_TICKET_UNREAD_MESSAGES",
                        payload: updatedTicket,
                        lastMessage: messageBody,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onCompanyGroupPermissions = (data) => {
            if (status === "group" && user?.profile !== "admin" && data.action === "update") {
                const hasUsers = data.users && Array.isArray(data.users) && data.users.length > 0;
                if (hasUsers) {
                    const isUserInGroup = data.users.some(u => u.id === user?.id);
                    if (!isUserInGroup) {
                        const ticketToRemove = ticketsListRef.current?.find(t => t.contactId === data.contactId);
                        if (ticketToRemove) {
                            dispatch({
                                type: "DELETE_TICKET",
                                payload: ticketToRemove.id,
                                status: status,
                                sortDir: sortTickets
                            });
                        }
                    }
                }
            }
        };

        const onParticipantLeftTicketsList = (data) => {
            if (data.userId === user?.id && !showAll) {
                dispatch({
                    type: "DELETE_TICKET",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-ticketParticipantLeft`, onParticipantLeftTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);
        socket.on(`company-${companyId}-groupPermissions`, onCompanyGroupPermissions);

        const handleTabMuteAll = (e) => {
            const { status: targetStatus, isMuted } = e.detail || {};
            const isGroupTab = targetStatus === "group";
            const isMatch = (targetStatus === status) || (isGroupTab && status === "group");
            if (isMatch) {
                dispatch({
                    type: "TAB_MUTE_ALL",
                    payload: { isMuted }
                });
            }
        };
        window.addEventListener("tabMuteAll", handleTabMuteAll);

        return () => {
            window.removeEventListener("tabMuteAll", handleTabMuteAll);
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-ticketParticipantLeft`, onParticipantLeftTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
            socket.off(`company-${companyId}-groupPermissions`, onCompanyGroupPermissions);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => ticket.status === status)
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            {isSelectionMode && (
                <div className={classes.selectionToolbar}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                            size="small"
                            checked={ticketsList.length > 0 && selectedTickets.length === ticketsList.length}
                            indeterminate={selectedTickets.length > 0 && selectedTickets.length < ticketsList.length}
                            onChange={handleSelectAll}
                            color="primary"
                            style={{ padding: 2 }}
                        />
                        <Typography variant="body2" className={classes.selectionText}>
                            {selectedTickets.length} selecionado(s)
                        </Typography>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Tooltip title="Silenciar selecionados">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={selectedTickets.length === 0}
                                    onClick={() => handleBatchMute(true)}
                                    style={{ color: selectedTickets.length > 0 ? "#d32f2f" : "#aaa", padding: 4 }}
                                >
                                    <NotificationsOffIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Reativar som dos selecionados">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={selectedTickets.length === 0}
                                    onClick={() => handleBatchMute(false)}
                                    style={{ color: selectedTickets.length > 0 ? "#388e3c" : "#aaa", padding: 4 }}
                                >
                                    <NotificationsActiveIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Fixar selecionados no topo">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={selectedTickets.length === 0}
                                    onClick={() => handleBatchPin(true)}
                                    style={{ color: selectedTickets.length > 0 ? "#f57c00" : "#aaa", padding: 4 }}
                                >
                                    <PushPinIcon fontSize="small" style={{ transform: "rotate(45deg)" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Desafixar selecionados">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={selectedTickets.length === 0}
                                    onClick={() => handleBatchPin(false)}
                                    style={{ color: selectedTickets.length > 0 ? "#757575" : "#aaa", padding: 4 }}
                                >
                                    <PushPinIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {status === "group" && user?.profile === "admin" && (
                            <Tooltip title="Permissões dos Grupos Selecionados">
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={selectedTickets.length === 0}
                                        onClick={() => setGroupPermissionsModalOpen(true)}
                                        style={{ color: selectedTickets.length > 0 ? "#1976d2" : "#aaa", padding: 4 }}
                                    >
                                        <SupervisorAccountIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Cancelar seleção">
                            <IconButton size="small" onClick={handleCancelSelection} style={{ color: "#555", padding: 4 }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            )}
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                    isSelected={selectedTickets.includes(ticket.id)}
                                    onToggleSelect={handleToggleSelect}
                                    selectionMode={isSelectionMode}
                                    onEnableSelection={handleEnableSelection}
                                />
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
            {groupPermissionsModalOpen && (
                <GroupPermissionsModal
                    open={groupPermissionsModalOpen}
                    onClose={() => setGroupPermissionsModalOpen(false)}
                    initialSelectedGroupIds={selectedGroupContactIds}
                />
            )}
        </Paper>
    );
};

export default TicketsListCustom;
